import React, { useEffect } from "react";
import { Grid, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { fileService } from "@services";
import { useAsync } from "@hooks";
import { File } from "@interfaces/File";
import FilePickerFileGridItem from "./FilePickerFileGridItem";

/** Все файлы папки одним запросом — вместо страниц обычная прокрутка (в папке
 * модели их десятки, редко сотни). */
const MAX_FILES = 1000;

type Props = {
    directoryId: number | null;
    isSelected(fileId: number): boolean;
    onSelectFile(file: File): void;
    /** увеличивается после успешной загрузки файла — триггерит рефетч списка */
    refreshKey: number;
    /** id файлов, уже занятых в этом атрибуте у другой строки — нельзя выбрать повторно */
    excludeFileIds?: number[];
    /** поиск файла по названию внутри выбранной папки */
    searchTerm?: string;
};

const FilePickerFileGrid = ({
    directoryId,
    isSelected,
    onSelectFile,
    refreshKey,
    excludeFileIds,
    searchTerm = "",
}: Props) => {
    const q = searchTerm.trim();

    const { data, loadingStatus, updateData } = useAsync(fileService.getFilesByDirectory, {
        functionArgs: [directoryId, { page: 1, page_size: MAX_FILES, ...(q ? { q } : {}) }],
        canLoad: !!directoryId,
        setLoadingStatusOnUpdate: false,
    });

    useEffect(() => {
        if (refreshKey) updateData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    if (!directoryId) {
        return <Typography color="textSecondary">Выберите папку слева</Typography>;
    }

    if (loadingStatus === "loading") {
        return (
            <Grid container spacing={1}>
                {[...new Array(6)].map((_, i) => (
                    <Grid item xs={4} md={3} key={i}>
                        <Skeleton variant="rect" height={100} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    const items = data?.result.items || [];

    if (!items.length) {
        return (
            <Typography color="textSecondary">
                {q ? "В этой папке нет файлов с таким названием" : "В этой папке нет файлов"}
            </Typography>
        );
    }

    return (
        <Grid container spacing={1}>
            {items.map((item) => (
                <FilePickerFileGridItem
                    key={item.id}
                    item={item}
                    selected={isSelected(item.id)}
                    disabled={excludeFileIds?.includes(item.id)}
                    disabledReason="Уже добавлено в этот атрибут"
                    onClick={onSelectFile}
                />
            ))}
        </Grid>
    );
};

export default FilePickerFileGrid;
